// Экран «Долги». Отвечает на один вопрос: что платить и в каком порядке.
//
// Порядок блоков — это приоритет действий, а не классификация:
//   1. Окно      — беспроцентные займы с датой сгорания. Единственное на экране,
//                  у чего есть дедлайн И цена пропуска. Поэтому оно сверху и
//                  единственное цветное.
//   2. Просрочка — факт, заведённый пользователем (kind='overdue').
//   3. Дни месяца— платежи сгруппированы по дню списания. Устойчиво к дырам в
//                  данных: день платежа известен всегда, «просрочен ли» — вывод.
//   4. Без графика, Закрытые.
//
// Группировки по state здесь намеренно нет: 25 из 31 долга списываются 5-го,
// а вывод о просрочке дрейфует на месяц за каждый месяц без событий — по нему
// нельзя строить структуру экрана.

import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../api/client';
import type { Debt } from '../api/types';
import { DebtFormSheet } from '../components/DebtFormSheet';
import { DebtModal } from '../components/DebtModal';
import { PayMonthlySheet } from '../components/PayMonthlySheet';
import { ChevronRightIcon } from '../components/icons';
import { DEBT_KINDS } from '../lib/debt-kinds';
import { haptic } from '../lib/telegram';
import { useViewCurrency } from '../lib/view-currency';

const MONTH_GEN = [
  'января',
  'февраля',
  'марта',
  'апреля',
  'мая',
  'июня',
  'июля',
  'августа',
  'сентября',
  'октября',
  'ноября',
  'декабря',
];

function today(): Date {
  const n = new Date();
  return new Date(Date.UTC(n.getFullYear(), n.getMonth(), n.getDate()));
}

function parseYmd(s: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return null;
  return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
}

function daysBetween(from: Date, to: Date): number {
  return Math.round((to.getTime() - from.getTime()) / 86_400_000);
}

function shortDate(d: Date): string {
  return `${d.getUTCDate()} ${MONTH_GEN[d.getUTCMonth()]?.slice(0, 3) ?? ''}`;
}

// «7 дней» / «завтра» / «сегодня» / «сгорело». Родительный падеж не мудрим —
// цифра + «дн» читается быстрее любой грамматики.
function daysLabel(days: number): string {
  if (days < 0) return 'сгорело';
  if (days === 0) return 'сегодня';
  if (days === 1) return 'завтра';
  return `${days} дн`;
}

// 1 платёж / 2 платежа / 5 платежей. Наивное «!==1 → платежей» даёт
// «2 платежей» — мелочь, по которой интерфейс читается как нерусский.
function plural(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 14) return many;
  if (mod10 === 1) return one;
  if (mod10 >= 2 && mod10 <= 4) return few;
  return many;
}

type Badge = { text: string; tone: 'burn' | 'verify' | 'warn' } | null;

// Ключевое различие всего экрана: «просрочен» и «мы не знаем» — разные вещи.
// Без записанных событий вывод по графику не может обвинять, поэтому он синий
// и зовёт свериться, а не красный.
function badgeFor(d: Debt): Badge {
  if (d.state === 'closed') return null;
  if (d.overdue_source === 'kind') return { text: 'просрочка', tone: 'burn' };
  if (d.overdue_source === 'missed_schedule') {
    return d.has_recorded_events
      ? { text: 'пропущен платёж', tone: 'warn' }
      : { text: 'нужно свериться', tone: 'verify' };
  }
  return null;
}

export function Debts(): JSX.Element {
  const { formatMoney, formatAmount, formatShortAmount } = useViewCurrency();
  const debtsQ = useQuery({ queryKey: ['debts'], queryFn: () => api.debts() });

  const [selectedDebt, setSelectedDebt] = useState<Debt | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showPayMonthly, setShowPayMonthly] = useState(false);
  const [openDays, setOpenDays] = useState<Set<string>>(new Set());

  if (debtsQ.isLoading) return <div className="loading">Загружаем…</div>;
  if (debtsQ.error)
    return <div className="error">Не удалось загрузить долги. Потяните вниз, чтобы повторить.</div>;
  if (!debtsQ.data) return <div className="error">Нет данных</div>;

  const all = debtsQ.data;
  const now = today();

  const closed = all.filter((d) => d.state === 'closed' || d.is_closed);
  const open = all.filter((d) => !(d.state === 'closed' || d.is_closed));

  const totalMinor = open.reduce((a, d) => a + BigInt(d.principal_remaining_minor), 0n);
  const monthlyMinor = open.reduce((a, d) => a + BigInt(d.monthly_payment_minor), 0n);

  // Окно: беспроцентные с ещё не сгоревшей датой. Сортируем по близости срока.
  const expiring = open
    .filter((d) => d.kind === 'uzum_zero' && d.due_date)
    .map((d) => ({ debt: d, due: parseYmd(d.due_date as string) }))
    .filter((x): x is { debt: Debt; due: Date } => x.due !== null)
    .map((x) => ({ ...x, days: daysBetween(now, x.due) }))
    .filter((x) => x.days >= 0)
    .sort((a, b) => a.days - b.days);

  const windowToClose = expiring.reduce((a, x) => a + BigInt(x.debt.principal_remaining_minor), 0n);
  const windowSaves = expiring.reduce((a, x) => a + BigInt(x.debt.prepay_savings_minor), 0n);
  const maxDays = expiring.length ? Math.max(...expiring.map((x) => x.days), 1) : 1;
  // Метки позиционируем в 8..92% трека, а не в 0..100%: подписи под крайними
  // точками иначе срезаются краем карточки. Сдвигать сами метки нельзя —
  // тогда точка уедет с линии срока и позиция начнёт врать.
  const markLeft = (days: number): string => `${8 + (days / maxDays) * 84}%`;

  const overdue = open.filter((d) => d.overdue_source === 'kind');

  // Дни списания. Просрочки уже показаны отдельным блоком наверху — если не
  // исключить их здесь, долг отрисуется дважды (у просрочки тоже есть день
  // списания). Долги без payment_day уходят в «Без графика».
  const rest = open.filter((d) => d.overdue_source !== 'kind');
  const scheduled = rest.filter((d) => d.payment_day != null);
  const unscheduled = rest.filter((d) => d.payment_day == null);
  const byDay = new Map<number, Debt[]>();
  for (const d of scheduled) {
    const day = d.payment_day as number;
    byDay.set(day, [...(byDay.get(day) ?? []), d]);
  }
  const dayGroups = [...byDay.entries()].sort((a, b) => a[0] - b[0]);

  const toggleDay = (key: string): void => {
    haptic('light');
    setOpenDays((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <div className="screen">
      <div className="list-toolbar">
        <h2 className="screen-title">Долги</h2>
        <button
          type="button"
          className="link-btn"
          onClick={() => {
            haptic('light');
            setShowCreate(true);
          }}
        >
          + Новый
        </button>
      </div>

      {expiring.length > 0 ? (
        <section className="window-card" aria-label="Беспроцентный период истекает">
          <div className="window-head">
            <span className="window-kicker">Беспроцентный период истекает</span>
            <span className="window-countdown">{daysLabel(expiring[0]?.days ?? 0)}</span>
          </div>

          <div className="window-track">
            <div className="window-line" />
            {expiring.map((x, i) => (
              <div
                key={x.debt.id}
                className={`window-mark ${x.days <= 7 ? 'near' : ''}`}
                style={{
                  left: markLeft(x.days),
                  animationDelay: `${i * 70}ms`,
                }}
              >
                <span className="window-dot" />
                <span className="window-date">{shortDate(x.due)}</span>
                <span className="window-amount">
                  {formatShortAmount(x.debt.principal_remaining_minor)}
                </span>
              </div>
            ))}
          </div>

          <div className="window-foot">
            <div>
              <div className="window-foot-label">Закрыть до срока</div>
              <div className="window-foot-value">{formatAmount(windowToClose, 'UZS')}</div>
            </div>
            <div className="window-foot-save">
              <div className="window-foot-label">Сэкономить</div>
              <div className="window-foot-value">{formatAmount(windowSaves, 'UZS')}</div>
            </div>
          </div>
        </section>
      ) : null}

      <div className="portfolio-line">
        <span className="portfolio-total">{formatMoney(totalMinor, 'UZS')}</span>
        <span className="portfolio-sep">·</span>
        <span className="portfolio-monthly">{formatAmount(monthlyMinor, 'UZS')} в месяц</span>
        <span className="portfolio-count">{open.length} активных</span>
      </div>

      {monthlyMinor > 0n ? (
        <button
          type="button"
          className="bulk-pay-btn"
          onClick={() => {
            haptic('medium');
            setShowPayMonthly(true);
          }}
        >
          Отметить платежи за месяц
        </button>
      ) : null}

      {overdue.length > 0 ? (
        <section className="burn-block">
          <h3 className="group-head group-head-burn">
            Просрочено
            <span className="group-count">{overdue.length}</span>
          </h3>
          <div className="debt-list">
            {overdue.map((d) => (
              <DebtRow key={d.id} debt={d} onClick={() => setSelectedDebt(d)} />
            ))}
          </div>
        </section>
      ) : null}

      {dayGroups.map(([day, list]) => {
        const key = `d${day}`;
        const isOpen = openDays.has(key);
        const sum = list.reduce((a, d) => a + BigInt(d.monthly_payment_minor), 0n);
        return (
          <section key={key} className="day-group">
            <button
              type="button"
              className={`day-head ${isOpen ? 'open' : ''}`}
              onClick={() => toggleDay(key)}
              aria-expanded={isOpen}
            >
              <span className="day-num">{day}</span>
              <span className="day-meta">
                <span className="day-title">числа</span>
                <span className="day-sub">
                  {list.length} {plural(list.length, 'платёж', 'платежа', 'платежей')}
                </span>
              </span>
              <span className="day-sum">{formatAmount(sum, 'UZS')}</span>
              <span className={`day-chevron ${isOpen ? 'open' : ''}`}>
                <ChevronRightIcon size={18} />
              </span>
            </button>
            {isOpen ? (
              <div className="debt-list">
                {list
                  .slice()
                  .sort((a, b) =>
                    BigInt(b.monthly_payment_minor) > BigInt(a.monthly_payment_minor) ? 1 : -1,
                  )
                  .map((d) => (
                    <DebtRow key={d.id} debt={d} onClick={() => setSelectedDebt(d)} />
                  ))}
              </div>
            ) : null}
          </section>
        );
      })}

      {unscheduled.length > 0 ? (
        <section className="day-group">
          <button
            type="button"
            className={`day-head ${openDays.has('none') ? 'open' : ''}`}
            onClick={() => toggleDay('none')}
            aria-expanded={openDays.has('none')}
          >
            <span className="day-num day-num-none">—</span>
            <span className="day-meta">
              <span className="day-title">Без графика</span>
              <span className="day-sub">день списания не задан</span>
            </span>
            <span className="day-sum">
              {formatAmount(
                unscheduled.reduce((a, d) => a + BigInt(d.principal_remaining_minor), 0n),
                'UZS',
              )}
            </span>
            <span className={`day-chevron ${openDays.has('none') ? 'open' : ''}`}>
              <ChevronRightIcon size={18} />
            </span>
          </button>
          {openDays.has('none') ? (
            <div className="debt-list">
              {unscheduled.map((d) => (
                <DebtRow key={d.id} debt={d} onClick={() => setSelectedDebt(d)} />
              ))}
            </div>
          ) : null}
        </section>
      ) : null}

      {closed.length > 0 ? (
        <details className="debt-closed-group">
          <summary>Закрытые ({closed.length})</summary>
          <div className="debt-list">
            {closed.map((d) => (
              <DebtRow key={d.id} debt={d} onClick={() => setSelectedDebt(d)} />
            ))}
          </div>
        </details>
      ) : null}

      {open.length === 0 && closed.length === 0 ? (
        <div className="empty-state">
          <div className="empty-title">Долгов нет</div>
          <div className="empty-hint">Добавьте обязательство кнопкой «+ Новый» наверху.</div>
        </div>
      ) : null}

      {selectedDebt ? (
        <DebtModal debt={selectedDebt} onClose={() => setSelectedDebt(null)} />
      ) : null}
      {showCreate ? <DebtFormSheet onClose={() => setShowCreate(false)} /> : null}
      {showPayMonthly ? (
        <PayMonthlySheet debts={open} onClose={() => setShowPayMonthly(false)} />
      ) : null}
    </div>
  );
}

// Срок возврата для строки списка. Намеренно не бейдж: бейджи на этом экране
// заняты состоянием платежей (badgeFor), и смешивать «пропущен платёж» с
// «пора отдавать целиком» нельзя — это разные действия.
function dueLabel(debt: Debt): { text: string; tone: string } | null {
  if (!debt.due_date || debt.state === 'closed' || debt.is_closed) return null;
  const due = parseYmd(debt.due_date);
  if (!due) return null;
  const now = today();
  const days = daysBetween(now, due);
  // Год дописываем, когда срок не в текущем году: у банковских кредитов дата
  // возврата уходит на 2028-й, и «10 сен» без года читается как ближайший сентябрь.
  const when =
    due.getUTCFullYear() === now.getUTCFullYear()
      ? shortDate(due)
      : `${shortDate(due)} ${due.getUTCFullYear()}`;
  if (days < 0) return { text: `срок вышел ${-days} дн назад`, tone: 'negative' };
  // У беспроцентных предстоящий срок уже показан карточкой «Окно» наверху —
  // там он с ценой пропуска, чего в строке не дать. Дублировать не будем;
  // сгоревший срок из «Окна» выпадает (там фильтр days >= 0), и тогда строка —
  // единственное место, где о нём вообще скажут.
  if (debt.kind === 'uzum_zero') return null;
  if (days === 0) return { text: 'вернуть сегодня', tone: 'warn' };
  if (days <= 14) return { text: `вернуть до ${when} · ${days} дн`, tone: 'warn' };
  return { text: `вернуть до ${when}`, tone: '' };
}

function DebtRow({ debt, onClick }: { debt: Debt; onClick: () => void }): JSX.Element {
  // В списке из тридцати долгов единица повторилась бы шестьдесят раз и увела
  // бы выравнивание: глаз ведёт по последнему знаку строки. Валюта объявлена
  // бейджем в углу и на итоге портфеля выше.
  const { formatAmount, formatMoney } = useViewCurrency();
  const kind = DEBT_KINDS[debt.kind];
  const badge = badgeFor(debt);
  const progress = debt.progress_pct;
  const due = dueLabel(debt);

  return (
    <button
      type="button"
      className={`debt-row ${debt.state === 'closed' ? 'closed' : ''}`}
      onClick={() => {
        haptic('light');
        onClick();
      }}
      aria-label={`Открыть детали: ${debt.title}`}
    >
      <div className="debt-row-head">
        <span className="debt-kind-icon" title={kind.label}>
          <kind.Icon size={16} />
        </span>
        <span className="debt-title">{debt.title}</span>
        {badge ? <span className={`state-badge ${badge.tone}`}>{badge.text}</span> : null}
        {debt.state === 'closed' ? <span className="state-badge dim">закрыт</span> : null}
        <span className="debt-chevron">
          <ChevronRightIcon size={16} />
        </span>
      </div>

      <div className="debt-row-body">
        <div className="debt-balance">
          {debt.currency === 'UZS'
            ? formatAmount(debt.principal_remaining_minor, debt.currency)
            : /* Чужая валюта в общем списке обязана называть себя. */
              formatMoney(debt.principal_remaining_minor, debt.currency)}
        </div>
        {BigInt(debt.monthly_payment_minor) > 0n ? (
          <div className="debt-monthly">
            по{' '}
            {debt.currency === 'UZS'
              ? formatAmount(debt.monthly_payment_minor, debt.currency)
              : formatMoney(debt.monthly_payment_minor, debt.currency)}
            /мес
          </div>
        ) : null}
      </div>

      {progress != null && progress > 0 ? (
        <div className="debt-progress">
          <div className="debt-progress-bar">
            <div className="debt-progress-fill" style={{ width: `${Math.min(100, progress)}%` }} />
          </div>
          <div className="debt-progress-label">{Math.round(progress)}% выплачено</div>
        </div>
      ) : null}

      {due ? <div className={`debt-due ${due.tone}`}>{due.text}</div> : null}

      {debt.note ? <div className="debt-note">{debt.note}</div> : null}
    </button>
  );
}
